import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Helmet } from "react-helmet";
import { InlineWidget } from "react-calendly";
import { useLocation } from "@reach/router";
import "fontsource-poppins/400.css";
import "fontsource-poppins/500.css";
import "fontsource-poppins/600.css";
import "fontsource-poppins/700.css";
import Modal from "../Modal/Modal";
import Nav from "../Nav/Nav";
import Footer from "../../containers/Footer/Footer";
import { getPathName, trackUser } from "../../helpers/helpers";
import { parse } from "query-string";
import ToTop from "../ToTop/ToTop";
import { debounce } from "lodash";

process.env.NODE_ENV !== "production" && import("preact/debug");

const CALENDLY_URL = {
	en: "https://calendly.com/theaiteam/discovery-call-30-minutes",
	pl: "https://calendly.com/botwiseio/30min",
};

export const LocaleContext = React.createContext();

const Layout = ({ pageContext: { locale, forPage }, showScrollArrow, children }) => {
	const [isModalOpened, setIsModalOpened] = useState(false);
	const [calendlyOpenedFrom, setCalendlyOpenedFrom] = useState("");
	const [customCalendlyUrl, setCustomCalendlyUrl] = useState(null);

	const [showScrollTopArrow, setShowScrollArrow] = useState(false);

	const { pathname, search } = useLocation();

	const query = parse(search);

	// eslint-disable-next-line consistent-return
	const setSession = useCallback(() => {
		if (query.redirect === `true`) {
			return pathname.includes(`tag/case-study`)
				? trackUser({
						eventName: `Session start from Landing Case study link`,
						tagManager: true,
						// eslint-disable-next-line
				  })
				: trackUser({
						eventName: `Session start from Landing blog link`,
						tagManager: true,
						// eslint-disable-next-line
				  });
		}
	}, [query.redirect, pathname]);

	useEffect(() => {
		setSession();
	}, [setSession]);

	const checkScroll = useCallback(
		debounce(() => {
			if (!showScrollTopArrow && window.pageYOffset > 400) {
				return setShowScrollArrow(true);
			}

			return setShowScrollArrow(false);
		}, 200),
		[],
	);

	useEffect(() => {
		window.addEventListener(`scroll`, checkScroll);

		() => window.removeEventListener(`scroll`, checkScroll);
	}, [checkScroll]);

	const toggleModal = (page = null, customCalendlyUrl = null) => {
		if (customCalendlyUrl) {
			setCustomCalendlyUrl(customCalendlyUrl);
		}

		if (page && !isModalOpened) {
			setCalendlyOpenedFrom(page);
		} else if (isModalOpened) {
			setCalendlyOpenedFrom("");
		}
		setIsModalOpened(!isModalOpened);
	};

	const closeCalendlyModal = () => {
		if (customCalendlyUrl) {
			setCustomCalendlyUrl(null);
		}

		toggleModal();
	};

	const isCalendlyEvent = e => {
		return e.data.event?.indexOf("calendly") === 0;
	};

	const logEvents = e => {
		if (isCalendlyEvent(e)) {
			if (e.data.event === "calendly.event_scheduled") {
				return trackUser({
					tagManager: true,
					eventName: `Event scheduled from ${calendlyOpenedFrom}`,
				});
			}
		}
	};

	useEffect(() => {
		window?.addEventListener("message", logEvents);

		return () => window?.removeEventListener("message", logEvents);
	}, [calendlyOpenedFrom]);

	return (
		<LocaleContext.Provider value={{ locale, forPage }}>
			<Helmet>
				<link
					rel="alternate"
					hrefLang="x-default"
					href={
						process.env.GATSBY_HOSTNAME +
						getPathName((locale === "en" ? "/pl" : "") + pathname, "en")
					}
				/>
				<link
					rel="alternate"
					hrefLang="pl"
					href={
						process.env.GATSBY_HOSTNAME +
						getPathName(
							locale === "pl"
								? (pathname.length === 3 ? "/" : "") + pathname.slice(3, pathname.length)
								: pathname,
							"pl",
						)
					}
				/>
				<body className={clsx(isModalOpened && "scrollOff")} />
				<script
					type="text/javascript"
					id="hs-script-loader"
					async
					defer
					src="//js.hs-scripts.com/14563637.js"
				></script>
			</Helmet>
			{showScrollTopArrow && showScrollArrow && <ToTop />}
			<Nav isPolish={locale === "pl"} toggleModal={toggleModal} />
			{React.cloneElement(children, { toggleModal, locale })}
			<Footer isPolish={locale === "pl"} />
			{isModalOpened && (
				<Modal closeModal={closeCalendlyModal}>
					<InlineWidget url={customCalendlyUrl || CALENDLY_URL[locale]} />
				</Modal>
			)}
		</LocaleContext.Provider>
	);
};

Layout.propTypes = {
	children: PropTypes.node.isRequired,
	pageContext: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.string]))
		.isRequired,
	showScrollArrow: PropTypes.bool,
};

Layout.defaultProps = {
	showScrollArrow: false,
};

export default Layout;
